import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AppShell } from "../components/AppShell";
import {
  PAGE_SIZE,
  TablePagination,
} from "../components/TablePagination";
import { fetchProfile } from "../api/profiles";
import {
  addLibrarySnippet,
  addLibrarySnippetsBulk,
  addUserSnippet,
  addUserSnippetsBulk,
  deleteLibrarySnippet,
  fetchLibrarySnippets,
  fetchUserSnippets,
  updateLibrarySnippet,
} from "../api/snippets";
import { MEDICAL_SPECIALTIES } from "../constants/profileOptions";
import { useLanguage } from "../i18n/LanguageContext";
import { t } from "../i18n";
import { specialtyLabel } from "./helpers";

const emptyForm = { text: "", specialty: "General Medicine" };

export const LibraryPage = () => {
  const { portal, language } = useLanguage();
  const copy = portal.library;
  const common = portal.common;
  const [profile, setProfile] = useState(null);
  const [snippets, setSnippets] = useState([]);
  const [userSnippets, setUserSnippets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [needsLanguage, setNeedsLanguage] = useState(false);
  const [success, setSuccess] = useState("");
  const [search, setSearch] = useState("");
  const [specialtyFilter, setSpecialtyFilter] = useState("");
  const [page, setPage] = useState(1);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [editingTimesUsed, setEditingTimesUsed] = useState(0);
  const [saving, setSaving] = useState(false);
  const [addingIds, setAddingIds] = useState(() => new Set());
  const [addingAll, setAddingAll] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [deletingFiltered, setDeletingFiltered] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    setNeedsLanguage(false);
    try {
      const nextProfile = await fetchProfile();
      setProfile(nextProfile);

      if (!nextProfile?.language) {
        setSnippets([]);
        setUserSnippets([]);
        setNeedsLanguage(true);
        setError(copy.needLanguage);
        return;
      }

      const [nextSnippets, nextUserSnippets] = await Promise.all([
        fetchLibrarySnippets(),
        fetchUserSnippets(),
      ]);
      setSnippets(Array.isArray(nextSnippets) ? nextSnippets : []);
      setUserSnippets(Array.isArray(nextUserSnippets) ? nextUserSnippets : []);
    } catch (err) {
      setError(err?.message || copy.loadFailed);
      setSnippets([]);
      setUserSnippets([]);
    } finally {
      setLoading(false);
    }
  }, [copy.loadFailed, copy.needLanguage]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const userSnippetTexts = useMemo(
    () =>
      new Set(
        userSnippets.map((snippet) => (snippet.text || "").toLowerCase())
      ),
    [userSnippets]
  );

  const specialtyFilteredSnippets = useMemo(() => {
    if (!specialtyFilter) return snippets;
    return snippets.filter(
      (snippet) => (snippet.specialty || "") === specialtyFilter
    );
  }, [snippets, specialtyFilter]);

  const filteredSnippets = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return specialtyFilteredSnippets;
    return specialtyFilteredSnippets.filter((snippet) => {
      const text = (snippet.text || "").toLowerCase();
      const specialty = (snippet.specialty || "").toLowerCase();
      return text.includes(query) || specialty.includes(query);
    });
  }, [specialtyFilteredSnippets, search]);

  const pageCount = Math.max(1, Math.ceil(filteredSnippets.length / PAGE_SIZE));

  useEffect(() => {
    setPage(1);
  }, [search, specialtyFilter]);

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const pagedSnippets = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredSnippets.slice(start, start + PAGE_SIZE);
  }, [filteredSnippets, page]);

  const missingSnippets = useMemo(
    () =>
      specialtyFilteredSnippets.filter(
        (snippet) => !userSnippetTexts.has((snippet.text || "").toLowerCase())
      ),
    [specialtyFilteredSnippets, userSnippetTexts]
  );

  const isAdmin = Boolean(profile?.is_admin);

  const lines = useMemo(
    () =>
      form.text
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean),
    [form.text]
  );

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setEditingTimesUsed(0);
  };

  const handleEdit = (snippet) => {
    if (!isAdmin) return;
    setEditingId(snippet._id);
    setEditingTimesUsed(snippet.times_used ?? 0);
    setForm({
      text: snippet.text || "",
      specialty: snippet.specialty || "General Medicine",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isAdmin || !form.text.trim()) return;

    const nextSpecialty = form.specialty.trim() || "General Medicine";
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      if (editingId) {
        await updateLibrarySnippet(editingId, {
          text: form.text.trim(),
          specialty: nextSpecialty,
          source: "library",
          times_used: editingTimesUsed,
        });
        setSuccess(copy.updated);
      } else if (lines.length === 1) {
        await addLibrarySnippet({
          text: lines[0],
          specialty: nextSpecialty,
        });
        setSuccess(copy.added);
      } else {
        const result = await addLibrarySnippetsBulk(form.text, nextSpecialty);
        const skipped = result.skipped_count || 0;
        const inserted = result.inserted_count || 0;
        const skippedNote =
          skipped > 0 ? t(copy.skippedNote, { count: skipped }) : "";
        setSuccess(
          t(copy.addedBulk, { count: inserted, skipped: skippedNote })
        );
      }
      resetForm();
      await loadData();
    } catch (err) {
      setError(err?.message || copy.saveFailed);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (snippet) => {
    if (!isAdmin) return;
    const confirmed = window.confirm(
      t(copy.confirmDelete, { text: snippet.text })
    );
    if (!confirmed) return;

    setError("");
    setSuccess("");
    try {
      await deleteLibrarySnippet(snippet._id);
      if (editingId === snippet._id) resetForm();
      setSuccess(copy.deleted);
      await loadData();
    } catch (err) {
      setError(err?.message || copy.deleteFailed);
    }
  };

  const handleDeleteFiltered = async () => {
    if (!isAdmin || filteredSnippets.length === 0 || deletingFiltered || deletingAll)
      return;
    const confirmed = window.confirm(
      t(copy.confirmDeleteFiltered, { count: filteredSnippets.length })
    );
    if (!confirmed) return;

    setDeletingFiltered(true);
    setError("");
    setSuccess("");
    try {
      const toDelete = [...filteredSnippets];
      await Promise.all(
        toDelete.map((snippet) => deleteLibrarySnippet(snippet._id))
      );
      if (editingId && toDelete.some((snippet) => snippet._id === editingId)) {
        resetForm();
      }
      setSuccess(t(copy.deletedFiltered, { count: toDelete.length }));
      await loadData();
    } catch (err) {
      setError(err?.message || copy.deleteFilteredFailed);
      await loadData();
    } finally {
      setDeletingFiltered(false);
    }
  };

  const handleDeleteAll = async () => {
    if (!isAdmin || snippets.length === 0 || deletingAll || deletingFiltered)
      return;
    const confirmed = window.confirm(
      t(copy.confirmDeleteAll, { count: snippets.length })
    );
    if (!confirmed) return;

    setDeletingAll(true);
    setError("");
    setSuccess("");
    try {
      const toDelete = [...snippets];
      await Promise.all(
        toDelete.map((snippet) => deleteLibrarySnippet(snippet._id))
      );
      resetForm();
      setSuccess(t(copy.deletedAll, { count: toDelete.length }));
      await loadData();
    } catch (err) {
      setError(err?.message || copy.deleteAllFailed);
      await loadData();
    } finally {
      setDeletingAll(false);
    }
  };

  const handleAddToPersonal = async (snippet) => {
    setAddingIds((prev) => new Set(prev).add(snippet._id));
    setError("");
    setSuccess("");
    try {
      await addUserSnippet({
        text: snippet.text,
        specialty: snippet.specialty || "general",
        source: "user",
        imported_from: "library",
      });
      setSuccess(t(copy.addedToPersonal, { text: snippet.text }));
      const nextUserSnippets = await fetchUserSnippets();
      setUserSnippets(Array.isArray(nextUserSnippets) ? nextUserSnippets : []);
    } catch (err) {
      setError(err?.message || copy.addFailed);
    } finally {
      setAddingIds((prev) => {
        const next = new Set(prev);
        next.delete(snippet._id);
        return next;
      });
    }
  };

  const handleAddAll = async () => {
    if (missingSnippets.length === 0) return;
    setAddingAll(true);
    setError("");
    setSuccess("");
    try {
      // Bulk API takes one specialty per request — group so library specialties are preserved.
      const bySpecialty = new Map();
      for (const snippet of missingSnippets) {
        const specialty = snippet.specialty || "general";
        const texts = bySpecialty.get(specialty);
        if (texts) {
          texts.push(snippet.text);
        } else {
          bySpecialty.set(specialty, [snippet.text]);
        }
      }

      let inserted = 0;
      let skipped = 0;
      for (const [specialty, texts] of bySpecialty) {
        const result = await addUserSnippetsBulk(texts.join("\n"), specialty, "library");
        inserted += result.inserted_count || 0;
        skipped += result.skipped_count || 0;
      }

      setSuccess(
        t(copy.addedBulk, {
          count: inserted,
          skipped: skipped
            ? t(copy.skippedOwned, { count: skipped })
            : "",
        })
      );
      const nextUserSnippets = await fetchUserSnippets();
      setUserSnippets(Array.isArray(nextUserSnippets) ? nextUserSnippets : []);
    } catch (err) {
      setError(err?.message || copy.addAllFailed);
    } finally {
      setAddingAll(false);
    }
  };

  return (
    <AppShell
      title={copy.title}
      description={isAdmin ? copy.descriptionAdmin : copy.descriptionUser}
    >
      {success && <div className="app-alert success">{success}</div>}

      {isAdmin && (
        <section className="app-form-card">
          <h2>{editingId ? copy.editTitle : copy.addTitle}</h2>
          <form onSubmit={handleSubmit} className="app-form">
            <label htmlFor="snippet-text">{copy.textLabel}</label>
            <textarea
              id="snippet-text"
              rows={editingId ? 3 : 5}
              value={form.text}
              onChange={(e) =>
                setForm((current) => ({ ...current, text: e.target.value }))
              }
              placeholder={
                editingId ? copy.placeholderEdit : copy.placeholderAdd
              }
              required
              disabled={saving || !profile?.language}
            />

            <label htmlFor="snippet-specialty">{copy.specialtyLabel}</label>
            <select
              id="snippet-specialty"
              value={form.specialty}
              onChange={(e) =>
                setForm((current) => ({
                  ...current,
                  specialty: e.target.value,
                }))
              }
              disabled={saving || !profile?.language}
            >
              {MEDICAL_SPECIALTIES.map((specialty) => (
                <option key={specialty} value={specialty}>
                  {specialtyLabel(specialty, language)}
                </option>
              ))}
            </select>

            {!editingId && lines.length > 1 && (
              <p className="app-hint">
                {t(copy.readyToAdd, { count: lines.length })}
              </p>
            )}

            <div className="app-form-actions">
              <button
                type="submit"
                className="app-primary"
                disabled={saving || !profile?.language}
              >
                {saving
                  ? common.saving
                  : editingId
                    ? copy.update
                    : lines.length > 1
                      ? t(copy.addMany, { count: lines.length })
                      : copy.addOne}
              </button>

              {editingId && (
                <button
                  type="button"
                  className="app-secondary"
                  onClick={resetForm}
                  disabled={saving}
                >
                  {common.cancel}
                </button>
              )}
            </div>
          </form>
        </section>
      )}

      <section className="app-table-card">
        <div className="app-toolbar">
          <input
            type="search"
            className="app-search"
            placeholder={copy.searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label={copy.searchAria}
          />
          <select
            className="app-filter"
            value={specialtyFilter}
            onChange={(e) => setSpecialtyFilter(e.target.value)}
            aria-label={copy.specialtyFilterAria}
          >
            <option value="">{copy.allSpecialties}</option>
            {MEDICAL_SPECIALTIES.map((specialty) => (
              <option key={specialty} value={specialty}>
                {specialtyLabel(specialty, language)}
              </option>
            ))}
          </select>
          <span className="app-count">
            {loading
              ? common.loading
              : `${filteredSnippets.length} ${common.of} ${snippets.length}`}
          </span>
          {(missingSnippets.length > 0 ||
            (isAdmin && snippets.length > 0)) && (
              <div className="app-toolbar-actions">
                {missingSnippets.length > 0 && (
                  <button
                    type="button"
                    className="app-secondary"
                    onClick={handleAddAll}
                    disabled={addingAll || loading}
                  >
                    {addingAll
                      ? common.adding
                      : t(copy.addAllMissing, { count: missingSnippets.length })}
                  </button>
                )}
                {isAdmin && snippets.length > 0 && (
                  <>
                    {(search.trim() || specialtyFilter) &&
                      filteredSnippets.length > 0 && (
                        <button
                          type="button"
                          className="app-secondary"
                          onClick={handleDeleteFiltered}
                          disabled={
                            deletingFiltered ||
                            deletingAll ||
                            loading ||
                            saving ||
                            addingAll
                          }
                        >
                          {deletingFiltered ? common.deleting : common.delete}
                        </button>
                      )}
                    <button
                      type="button"
                      className="app-secondary"
                      onClick={handleDeleteAll}
                      disabled={
                        deletingAll ||
                        deletingFiltered ||
                        loading ||
                        saving ||
                        addingAll
                      }
                    >
                      {deletingAll ? common.deleting : copy.deleteAll}
                    </button>
                  </>
                )}
              </div>
            )}
        </div>

        <div className="app-table-wrap">
          <table className="app-table">
            <thead>
              <tr>
                <th className="app-text-cell">{copy.colText}</th>
                <th className="app-specialty-cell">{copy.colSpecialty}</th>
                <th className="app-actions-cell">{copy.colActions}</th>
              </tr>
            </thead>
            <tbody>
              {!loading && filteredSnippets.length === 0 && (
                <tr>
                  <td colSpan={3} className="app-empty">
                    {copy.empty}
                  </td>
                </tr>
              )}
              {pagedSnippets.map((snippet) => {
                const owned = userSnippetTexts.has(
                  (snippet.text || "").toLowerCase()
                );
                return (
                  <tr key={snippet._id}>
                    <td className="app-text-cell">{snippet.text}</td>
                    <td className="app-specialty-cell">
                      {specialtyLabel(snippet.specialty, language)}
                    </td>
                    <td className="app-actions-cell">
                      {owned ? (
                        <span
                          style={{ color: "green", marginRight: "10px" }}
                          className="app-count"
                        >
                          {copy.addedLabel}{" "}
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="app-link-btn"
                          onClick={() => handleAddToPersonal(snippet)}
                          disabled={addingIds.has(snippet._id)}
                        >
                          {addingIds.has(snippet._id)
                            ? common.adding
                            : common.add}
                        </button>
                      )}
                      {isAdmin && (
                        <>
                          <button
                            type="button"
                            className="app-link-btn"
                            onClick={() => handleEdit(snippet)}
                          >
                            {common.edit}
                          </button>
                          <button
                            type="button"
                            className="app-link-btn danger"
                            onClick={() => handleDelete(snippet)}
                          >
                            {common.delete}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <TablePagination
          page={page}
          pageCount={pageCount}
          onPageChange={setPage}
          labels={{
            previous: common.previous,
            next: common.next,
            pageStatus: common.pageStatus,
            aria: common.paginationAria,
          }}
        />
      </section>
    </AppShell>
  );
};
